import { View, StyleSheet, Text, Pressable, Image } from "react-native";
import { useUISlice } from "@/models/useUISlice";
import ModalContainer from "@/ui/ModalContainer";
import { ms } from "@/ui/moderateScale";
import { useTranslation } from "react-i18next";
import {
  Colors,
  REVENUE_CAT_ANDROID_API_KEY,
  REVENUE_CAT_IOS_API_KEY,
} from "@/constants";
import PrimaryButton from "./PrimaryButton";
import { useEffect, useState } from "react";
import Icon from "./Icon";
import { useAuth } from "@/models/useAuth";
import { App } from "@capacitor/app";
import { SubscriptionPlan } from "@/models/types";
import { useUserSlice } from "@/models/useUserSlice";
import { getPlatform, isNativeApp } from "@/models/platform";
import { Purchases, PurchasesPackage } from "@revenuecat/purchases-capacitor";
import { gotoPrivacy, gotoTerms } from "@/models/utils";

type PlanOption = {
  title: string;
  price: string;
  discount?: string;
  plan: SubscriptionPlan;
};

const Feature = ({ textKey }: { textKey: string }) => {
  const { t } = useTranslation();

  return (
    <View style={styles.featureContainer}>
      <Icon icon="tickCircle" style={styles.tickIcon} />
      <Text style={styles.featureText}>{t(textKey)}</Text>
    </View>
  );
};

const PlanSelector = ({
  plan,
  selectedPlan,
  onSelect,
}: {
  plan: PlanOption;
  selectedPlan: SubscriptionPlan;
  onSelect: (plan: SubscriptionPlan) => void;
}) => {
  const isSelected = selectedPlan === plan.plan;
  const { t } = useTranslation();

  return (
    <Pressable
      key={plan.title}
      style={[
        styles.planButton,
        { borderColor: isSelected ? Colors.accent : Colors.text + "30" },
      ]}
      onPress={() => onSelect(plan.plan)}
    >
      <View style={isSelected ? styles.radioOuterSelected : styles.radioOuter}>
        <View
          style={isSelected ? styles.radioInnerSelected : styles.radioInner}
        />
      </View>
      <View style={styles.planContent}>
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <Text style={styles.planTitle}>{plan.title}</Text>
          {plan.discount && (
            <Text style={styles.discountText}>{plan.discount}</Text>
          )}
        </View>
        <Text style={styles.planPrice}>
          {plan.price}
          <Text
            style={{
              marginLeft: ms(1),
              fontSize: ms(12),
              color: Colors.text + "60",
            }}
          >
            {plan.plan === "YEARLY_MID_TIER"
              ? t("alertWidgetPaywall.perYear")
              : plan.plan === "WEEKLY_MID_TIER"
                ? t("alertWidgetPaywall.perWeek")
                : t("alertWidgetPaywall.perMonth")}
          </Text>
        </Text>
      </View>
    </Pressable>
  );
};

const AlertWidgetPaywall = () => {
  const visible = useUISlice((state) => state.paywallVisible);
  const setVisible = useUISlice((state) => state.setPaywallVisible);
  const [selectedPlan, setSelectedPlan] =
    useState<SubscriptionPlan>("YEARLY_MID_TIER");
  const { t } = useTranslation();
  const width = useUISlice((state) => state.device.width);
  const revenueCat = useUISlice((state) => state.revenueCat);

  useEffect(() => {
    const listenBackButton = () => {
      App.addListener("backButton", () => {
        setVisible(undefined);
      });
    };

    const configureRevenueCat = async () => {
      if (isNativeApp() && !revenueCat && visible) {
        AlertWidgetPaywall.configureRevenueCat();
      }
    };

    configureRevenueCat();
    listenBackButton();

    return () => {
      App.removeAllListeners();
    };
  }, [visible]);

  let plans: PlanOption[] = [
    {
      title: t("alertWidgetPaywall.monthly"),
      price: "US$19.99",
      plan: "MONTHLY_MID_TIER",
    },
    {
      title: t("alertWidgetPaywall.annual"),
      discount: t("alertWidgetPaywall.discountText", {
        discount: "US$120",
      }),
      price: "US$119.99",
      plan: "YEARLY_MID_TIER",
    },
  ];

  if (revenueCat?.packages?.length > 0) {
    plans = revenueCat.packages.map((pkg) => {
      if (pkg.identifier.includes("weekly")) {
        return {
          title: t("alertWidgetPaywall.weekly"),
          price: pkg.product.priceString,
          plan: "WEEKLY_MID_TIER",
        };
      }

      if (pkg.identifier.includes("monthly")) {
        return {
          title: t("alertWidgetPaywall.monthly"),
          price: pkg.product.priceString,
          plan: "MONTHLY_MID_TIER",
        };
      }

      if (pkg.identifier.includes("annual")) {
        return {
          title: t("alertWidgetPaywall.annual"),
          price: pkg.product.priceString,
          discount: t("alertWidgetPaywall.discountText", {
            discount: "$560",
          }),
          plan: "YEARLY_MID_TIER",
        };
      }
    });
  }

  const onPress = async () => {
    const onPlanSelected = async () => {
      if (isNativeApp()) {
        const pkgs: { [key: string]: PurchasesPackage } = {
          YEARLY_MID_TIER: revenueCat.packages.find((pkg) =>
            pkg.identifier.includes("annual")
          ),
          WEEKLY_MID_TIER: revenueCat.packages.find((pkg) =>
            pkg.identifier.includes("weekly")
          ),
          MONTHLY_MID_TIER: revenueCat.packages.find((pkg) =>
            pkg.identifier.includes("monthly")
          ),
        };
        useUserSlice
          .getState()
          .subscription()
          .revenueCatPurchase(pkgs[selectedPlan]);
      } else {
        useUserSlice.getState().subscription().stripeCheckout(selectedPlan);
      }
    };

    if (!useAuth.isAuthenticated()) {
      useAuth.signIn({
        callback: () => {
          onPlanSelected();
        },
      });

      return;
    }

    onPlanSelected();
  };

  const _gotoTerms = () => {
    App.removeAllListeners();
    gotoTerms();
  };

  const _gotoPrivacy = () => {
    App.removeAllListeners();
    gotoPrivacy();
  };

  if (!visible) return null;

  if (isNativeApp() && !revenueCat) return null;

  return (
    <ModalContainer
      shouldPersistBackdropClick
      title={t("alertWidgetPaywall.title")}
      subtitle={t("alertWidgetPaywall.subtitle")}
      onDismiss={() => setVisible(undefined)}
      hideCancel
      modalWidth={Math.min(width, 600)}
    >
      <View style={styles.features}>
        <Feature textKey="alertWidgetPaywall.feature1" />
        <Feature textKey="alertWidgetPaywall.feature2" />
        <Feature textKey="alertWidgetPaywall.feature3" />
        <Feature textKey="alertWidgetPaywall.feature4" />
        <Feature textKey="alertWidgetPaywall.feature5" />
      </View>

      <View style={styles.plansContainer}>
        {plans.map((plan) => (
          <PlanSelector
            key={plan.plan}
            plan={plan}
            selectedPlan={selectedPlan}
            onSelect={setSelectedPlan}
          />
        ))}
      </View>

      <PrimaryButton text={t("alertWidgetPaywall.button")} onPress={onPress} />

      {!isNativeApp() && (
        <View style={styles.joinContainer}>
          <View style={styles.avatarsContainer}>
            <Image
              source={{ uri: "/icons/paywall_avatar1.jpg" }}
              style={[styles.avatar, { marginLeft: 0 }]}
            />
            <Image
              source={{ uri: "/icons/paywall_avatar2.jpg" }}
              style={styles.avatar}
            />
            <Image
              source={{ uri: "/icons/paywall_avatar3.jpg" }}
              style={styles.avatar}
            />
          </View>

          <Text style={styles.joinText}>
            {t("alertWidgetPaywall.joinText")}
          </Text>
        </View>
      )}

      {isNativeApp() && (
        <View style={styles.footer}>
          <Text onPress={_gotoTerms} style={styles.footerLink}>
            {t("alertWidgetPaywall.terms")}
          </Text>
          <Text onPress={_gotoPrivacy} style={styles.footerLink}>
            {t("alertWidgetPaywall.privacy")}
          </Text>
          <Text
            onPress={
              useUserSlice.getState().subscription().revenueCatRestorePurchase
            }
            style={styles.footerLink}
          >
            {t("alertWidgetPaywall.restore")}
          </Text>
        </View>
      )}
      <View style={styles.negativeMargin} />
    </ModalContainer>
  );
};

AlertWidgetPaywall.configureRevenueCat = async () => {
  if (isNativeApp()) {
    const user = useAuth.currentAuthUser();

    Purchases.configure({
      appUserID: user?.userId,
      apiKey:
        getPlatform() === "ios"
          ? REVENUE_CAT_IOS_API_KEY
          : REVENUE_CAT_ANDROID_API_KEY,
    }).then(async () => {
      const customerInfo = await Purchases.getCustomerInfo().then(
        (res) => res.customerInfo
      );

      const offerings = await Purchases.getOfferings();

      useUISlice.getState().setRevenueCat({
        packages: offerings?.current?.availablePackages?.slice(0, 2) || [],
        customerInfo,
      });

      if (customerInfo?.activeSubscriptions) {
        useUserSlice
          .getState()
          .setIsSubscribed(customerInfo?.activeSubscriptions?.length > 0);
      }
    });
  }
};

const styles = StyleSheet.create({
  negativeMargin: {
    marginBottom: -ms(12),
  },
  features: {
    marginBottom: ms(16),
  },
  featureContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: ms(8),
    marginBottom: ms(6),
  },
  discountText: {
    fontSize: ms(12),
    fontWeight: "500",
    lineHeight: ms(24),
    color: Colors.text,
    paddingHorizontal: ms(12),
    paddingVertical: ms(1),
    backgroundColor: "#00ff00" + "20",
    borderRadius: ms(8),
    marginLeft: ms(12),
  },
  avatar: {
    borderWidth: ms(2),
    borderColor: Colors.text + "30",
    borderRadius: ms(24),
    width: ms(18),
    height: ms(18),
    marginLeft: -ms(2),
  },
  featureText: {
    fontSize: ms(14),
    fontWeight: "400",
    lineHeight: ms(24),
    color: Colors.text,
  },
  tickIcon: {
    tintColor: "#15803d",
    width: ms(16),
    height: ms(16),
  },
  planButton: {
    borderWidth: ms(2),
    height: ms(60),
    paddingHorizontal: ms(16),
    borderRadius: ms(12),
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: ms(12),
  },
  radioOuter: {
    width: ms(18),
    height: ms(18),
    borderRadius: ms(16),
    backgroundColor: Colors.text + "30",
    alignItems: "center",
    justifyContent: "center",
  },
  radioInner: {
    width: ms(14),
    height: ms(14),
    backgroundColor: Colors.white,
    borderRadius: ms(8),
  },
  radioOuterSelected: {
    width: ms(18),
    height: ms(18),
    borderRadius: ms(16),
    backgroundColor: Colors.accent,
    alignItems: "center",
    justifyContent: "center",
  },
  radioInnerSelected: {
    width: ms(8),
    height: ms(8),
    backgroundColor: Colors.white,
    borderRadius: ms(8),
  },
  planContent: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingLeft: ms(12),
  },
  planTitle: {
    fontSize: ms(16),
    fontWeight: "400",
    color: Colors.text,
  },
  planPrice: {
    fontSize: ms(14),
    color: Colors.text,
  },
  plansContainer: {
    marginVertical: ms(16),
  },
  joinContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: ms(12),
    gap: ms(4),
  },
  avatarsContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: -ms(4),
  },
  joinText: {
    marginLeft: ms(8),
    fontSize: ms(12),
    color: Colors.subtitle,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "center",
    gap: ms(32),
    marginTop: ms(12),
  },
  footerLink: {
    fontSize: ms(14),
    color: Colors.subtitle,
  },
});

export default AlertWidgetPaywall;
